/**
 * useKgLayout
 *
 * Owns everything related to positioning and zoom:
 *   1. Runs ELK (async) on mount or whenever initialNodes/initialEdges change.
 *   2. Shows a loading state while ELK computes (full overlay on first load only).
 *   3. Calls fitView after ELK is done so all nodes are visible.
 *   4. Tracks the current zoom level.
 *   5. Injects `data.collapsed = true` on every node when zoom < threshold.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useEdgesState, useNodesState, useReactFlow } from "@xyflow/react";
import {
  KG_GRAPH_REFINE_FIT_DURATION_MS,
  KG_GRAPH_REFINE_FIT_MAX_ZOOM,
  KG_GRAPH_REFINE_FIT_PADDING,
} from "../kgViewport.constants";
import { getDagreLayout } from "../utils/dagreLayout";
import type { KgFlowEdge, KgFlowNode } from "../types";

const fitPaddingForCount = (count: number): number => {
  if (count <= 20) return 0.18;
  if (count <= 50) return 0.12;
  return 0.08;
};

interface UseKgLayoutOptions {
  initialNodes: KgFlowNode[];
  initialEdges: KgFlowEdge[];
  collapsedZoomThreshold: number;
  /** Changes when search/filter query changes — triggers refine layout after first paint. */
  layoutQueryKey?: string;
  /** Live canvas height (px) — drives column wrapping for non-table layers. */
  viewportHeight?: number;
}

export interface UseKgLayoutResult {
  nodes: KgFlowNode[];
  edges: KgFlowEdge[];
  onNodesChange: ReturnType<typeof useNodesState<KgFlowNode>>[2];
  onEdgesChange: ReturnType<typeof useEdgesState<KgFlowEdge>>[2];
  /** True on first ELK run — full "Building knowledge graph…" overlay. */
  isLayouting: boolean;
  /** True when re-layouting after search/filter (subtle canvas fade, no full overlay). */
  isRefiningLayout: boolean;
  zoom: number;
  setZoom: (zoom: number) => void;
  zoomAwareNodes: KgFlowNode[];
}

export const useKgLayout = ({
  initialNodes,
  initialEdges,
  collapsedZoomThreshold,
  layoutQueryKey,
  viewportHeight,
}: UseKgLayoutOptions): UseKgLayoutResult => {
  const [nodes, setNodes, onNodesChange] =
    useNodesState<KgFlowNode>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<KgFlowEdge>(initialEdges);
  const [zoom, setZoom] = useState(0.4);
  const [isLayouting, setIsLayouting] = useState(true);
  const [isRefiningLayout, setIsRefiningLayout] = useState(false);
  const hasLaidOutOnceRef = useRef(false);
  const { fitView } = useReactFlow();

  useEffect(() => {
    let cancelled = false;
    let refineDelayTimer: ReturnType<typeof setTimeout> | undefined;
    const isRefine = hasLaidOutOnceRef.current;

    if (isRefine) {
      refineDelayTimer = setTimeout(() => {
        if (!cancelled) {
          setIsRefiningLayout(true);
        }
      }, 280);
    } else {
      setIsLayouting(true);
    }

    try {
      const layoutedNodes = getDagreLayout(initialNodes, initialEdges, {
        direction: "LR",
        viewportHeight,
        baseRows: 3,
        baseCols: 5,
      });
      if (cancelled) return;
      setNodes(layoutedNodes);
      setEdges(initialEdges);
      hasLaidOutOnceRef.current = true;

      const padding = isRefine
        ? KG_GRAPH_REFINE_FIT_PADDING
        : fitPaddingForCount(layoutedNodes.length);
      const duration = isRefine ? KG_GRAPH_REFINE_FIT_DURATION_MS : 400;
      const maxZoom = isRefine ? KG_GRAPH_REFINE_FIT_MAX_ZOOM : undefined;

      requestAnimationFrame(() => {
        if (cancelled) return;
        setTimeout(() => {
          if (cancelled) return;
          void fitView({
            padding,
            duration,
            ...(maxZoom !== undefined ? { maxZoom } : {}),
          });
          setIsLayouting(false);
          setIsRefiningLayout(false);
        }, 150);
      });
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("[KgCanvas] Dagre layout failed:", err);
      }
      if (!cancelled) {
        setIsLayouting(false);
        setIsRefiningLayout(false);
      }
    }

    return () => {
      cancelled = true;
      if (refineDelayTimer) {
        clearTimeout(refineDelayTimer);
      }
      setIsRefiningLayout(false);
    };
  }, [
    initialNodes,
    initialEdges,
    layoutQueryKey,
    viewportHeight,
    setNodes,
    setEdges,
    fitView,
  ]);

  const zoomAwareNodes = useMemo(() => {
    const isCollapsed = zoom < collapsedZoomThreshold;
    return nodes.map((node) =>
      node.data.collapsed === isCollapsed
        ? node
        : { ...node, data: { ...node.data, collapsed: isCollapsed } },
    );
  }, [nodes, zoom, collapsedZoomThreshold]);

  return {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    isLayouting,
    isRefiningLayout,
    zoom,
    setZoom,
    zoomAwareNodes,
  };
};
